import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppsRounded, Add, Search } from "@mui/icons-material";
import { InputBase, Popover } from "@mui/material";
import { toast } from "react-toastify";
import { useGetAllTopCategoriesQuery } from "../../Store/Services/CategoryService";
import {
  useLazyGetProductsByIdQuery,
  useLazyGetAllSearchedProductsQuery,
} from "../../Store/Services/ProductService";
import { useAddToCartMutation } from "../../Store/Services/CartService";
import { addItemToTable } from "../../Store/Slices/restaurantSlice";

import { getCdnImageUrl } from "../../Utils/cdn";
import { getTableCartId, setTableCartId } from "../../Utils/cartSession";
import styles from "./Restaurant.module.css";

const normalize = (value) => String(value ?? "").trim();

// Product-detail responses expose variants in several possible shapes.
// Normalize any of them into { GroupName: [value, value, ...] }.
const extractVariantGroups = (detail) => {
  if (!detail) return {};
  const source =
    detail.variants ||
    detail.variations ||
    detail.prodVariations ||
    detail.variantList ||
    detail.productVariants ||
    detail?.list?.variants ||
    (Array.isArray(detail?.list) ? detail.list[0]?.variants : null) ||
    [];

  const groups = {};
  const push = (name, value) => {
    const key = normalize(name);
    const val = normalize(value);
    if (!key || !val) return;
    if (!groups[key]) groups[key] = [];
    if (!groups[key].includes(val)) groups[key].push(val);
  };

  (Array.isArray(source) ? source : []).forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    // Flat pair shape: { variant, variantValue }
    if (entry.variant !== undefined && entry.variantValue !== undefined) {
      push(entry.variant, entry.variantValue);
      return;
    }
    // Grouped shape: { variantName / name, variantValues / values / options }
    const name = entry.variantName ?? entry.name ?? entry.type ?? entry.title;
    const values =
      entry.variantValues ?? entry.values ?? entry.options ?? entry.valueList;
    if (name && Array.isArray(values)) {
      values.forEach((v) =>
        push(name, typeof v === "object" ? v.value ?? v.variantValue ?? v.name : v)
      );
    } else if (name && (entry.value ?? entry.variantValue)) {
      push(name, entry.value ?? entry.variantValue);
    }
  });

  return groups;
};

const RestaurantMenu = ({ tableId }) => {
  const dispatch = useDispatch();
  const currency = useSelector((state) => state.currency);
  // Online and phone orders are tracked under synthetic ids ("online-..." /
  // "phone-...") because no table is involved, so there is nothing to send the
  // backend as a table.
  const virtualTable = useSelector(
    (state) => state.restaurant.tables?.find((t) => t.id === tableId) ?? null
  );
  const isVirtualTable = Boolean(virtualTable?.isOnline);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [search, setSearch] = useState("");
  const [variantAnchor, setVariantAnchor] = useState(null);
  const [variantItem, setVariantItem] = useState(null);
  const [variantSelections, setVariantSelections] = useState({});

  const { data: categories, isSuccess: categoriesSuccess } =
    useGetAllTopCategoriesQuery();
  const [fetchProductById] = useLazyGetProductsByIdQuery();
  const [fetchProductsByCategory, { data: productsData }] =
    useLazyGetAllSearchedProductsQuery();
  const [addToCart] = useAddToCartMutation();

  const categoriesList = categories?.list || [];

  const getList = (list) => (Array.isArray(list) ? list.flat() : []);
  const itemsList = getList(
    productsData?.list ??
      productsData?.data ??
      productsData?.products ??
      productsData
  );

  useEffect(() => {
    fetchProductsByCategory(`?pageNumber=1&pageSize=50`);
  }, [fetchProductsByCategory]);

  // Maps a category id to its display name so each order line can be tagged
  // with the kitchen station (category) that should prepare it.
  const categoryNameById = useMemo(() => {
    const map = {};
    categoriesList.forEach((category) => {
      const name = normalize(category.catName);
      if (!name) return;
      [category.prodCatUuid, category.prodCatUUID, category.catId].forEach(
        (id) => {
          const key = normalize(id);
          if (key) map[key] = name;
        }
      );
    });
    return map;
  }, [categoriesList]);

  const resolveStation = (item) => {
    const directName = normalize(
      item?.category?.catName ?? item?.catName ?? item?.categoryName
    );
    if (directName) return directName;

    const catId = normalize(
      item?.prodCatUuid ??
        item?.prodCatUUID ??
        item?.category?.prodCatUuid ??
        item?.category?.prodCatUUID ??
        item?.catId ??
        item?.categoryId ??
        item?.topCatId ??
        item?.category?.catId
    );
    if (catId && categoryNameById[catId]) return categoryNameById[catId];

    if (selectedCategory && categoryNameById[selectedCategory]) {
      return categoryNameById[selectedCategory];
    }
    return "General";
  };

  const handleCategorySelect = (categoryValue) => {
    setSelectedCategory(categoryValue);
    if (categoryValue) {
      fetchProductsByCategory(
        `?Category=${encodeURIComponent(categoryValue)}&pageNumber=1&pageSize=50`
      );
    } else {
      fetchProductsByCategory(`?pageNumber=1&pageSize=50`);
    }
  };

  const visibleItems = useMemo(() => {
    let list = itemsList;

    if (search.trim()) {
      const term = search.trim().toLowerCase();
      list = list.filter((item) =>
        normalize(item?.title).toLowerCase().includes(term)
      );
    }
    return list;
  }, [itemsList, search]);

  const handleAdd = async (item, variants) => {
    if (!tableId) return false;

    if (item.quantity === 0) {
      toast.error("Out of stock", { position: "bottom-right" });
      return false;
    }

    const currentCartId = getTableCartId(tableId);
    const dataSet = {
      orderType: 2,
      tableId: isVirtualTable ? null : tableId,
      prodDefId: item.prodDefId,
      qty: 1,
      orderHeadId:
        !currentCartId || currentCartId === "undefined" ? 0 : currentCartId,
      prodHeaderId: 0,
    };

    // Phone and online orders have no table, so the backend needs to know how
    // the order arrived and (for phone orders) which customer placed it.
    if (isVirtualTable) {
      dataSet.orderMtd = virtualTable?.isPhone ? "PHONE" : "ONLINE";
      dataSet.custId = virtualTable?.isPhone
        ? virtualTable?.custId ?? null
        : null;
    }

    try {
      const userCartId = await addToCart(dataSet).unwrap();
      setTableCartId(tableId, userCartId.result);
      window.dispatchEvent(
        new CustomEvent("pos:cart-id-changed", {
          detail: { cartId: userCartId.result, tableId },
        })
      );

      dispatch(
        addItemToTable({
          tableId,
          item: {
            prodDefId: item.prodDefId,
            title: item.title,
            station: resolveStation(item),
            price: item.price,
            discount: item.discount,
            image: item.imageList ? item.imageList.split(",")[0] : "",
            variants: variants || item.variants || [],
          },
        })
      );
      return true;
    } catch (err) {
      console.error("Failed to add to cart from restaurant menu: ", err);
      toast.error("Error, something went wrong!", { position: "bottom-right" });
      return false;
    }
  };

  // Groups the currently opened item's variants for display in the popover.
  const groupVariants = (item) => item?.variantGroups || {};

  const handleItemClick = async (event, item) => {
    if (item.quantity === 0) return;
    const anchor = event.currentTarget;

    // Try to resolve variant options from the full product detail.
    let groups = extractVariantGroups(item);
    if (Object.keys(groups).length === 0) {
      try {
        const detail = await fetchProductById(
          item.prodDefUUID ?? item.prodDefId
        ).unwrap();
        groups = extractVariantGroups(detail);
      } catch {
        groups = {};
      }
    }

    if (Object.keys(groups).length > 0) {
      const defaults = {};
      Object.keys(groups).forEach((name) => {
        defaults[name] = groups[name][0];
      });
      setVariantSelections(defaults);
      setVariantItem({ ...item, variantGroups: groups });
      setVariantAnchor(anchor);
    } else {
      handleAdd(item);
    }
  };

  const closeVariantPopover = () => {
    setVariantAnchor(null);
    setVariantItem(null);
    setVariantSelections({});
  };

  const confirmVariantAdd = async () => {
    if (!variantItem) return;
    const variants = Object.entries(variantSelections).map(
      ([variant, variantValue]) => ({ variant, variantValue })
    );
    await handleAdd(variantItem, variants);
    closeVariantPopover();
  };

  return (
    <div className={styles.menuPane}>
      <div className={styles.menuSearchRow}>
        <div className={styles.menuSearchBox}>
          <Search className={styles.menuSearchIcon} />
          <InputBase
            placeholder="Search menu item"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className={styles.menuSearchInput}
          />
        </div>
      </div>

      <div className={styles.menuCategoryRow}>
        <button
          className={`${styles.categoryChip} ${
            !selectedCategory ? styles.categoryChipActive : ""
          }`}
          onClick={() => handleCategorySelect(null)}
        >
          <span className={styles.chipAllIcon}>
            <AppsRounded fontSize="small" />
          </span>
          <span>All</span>
        </button>
        {categoriesSuccess &&
          categoriesList.map((category) => (
            <button
              key={category.prodCatUuid ?? category.prodCatUUID ?? category.catId}
              className={`${styles.categoryChip} ${
                selectedCategory ===
                normalize(
                  category.prodCatUuid ??
                    category.prodCatUUID ??
                    category.catId
                )
                  ? styles.categoryChipActive
                  : ""
              }`}
              onClick={() =>
                handleCategorySelect(
                  normalize(
                    category.prodCatUuid ??
                      category.prodCatUUID ??
                      category.catId
                  )
                )
              }
            >
              {category.imagesList && category.imagesList.split(",")[0] ? (
                <img
                  className={styles.chipImage}
                  src={getCdnImageUrl(category.imagesList.split(",")[0])}
                  alt={category.catName}
                />
              ) : (
                <span className={styles.chipAllIcon}>
                  <AppsRounded fontSize="small" />
                </span>
              )}
              <span>{category.catName}</span>
            </button>
          ))}
      </div>

      <div className={styles.menuGrid}>
        {visibleItems.length === 0 ? (
          <p className={styles.emptyMenu}>No menu items found.</p>
        ) : (
          visibleItems.map((item) => (
            <button
              key={item.prodDefId}
              className={`${styles.menuCard} ${
                item.quantity === 0 ? styles.menuCardDisabled : ""
              }`}
              onClick={(event) => handleItemClick(event, item)}
              disabled={item.quantity === 0}
            >
              <div className={styles.menuImageWrap}>
                {item.imageList && item.imageList.split(",")[0] ? (
                  <img
                    src={getCdnImageUrl(item.imageList.split(",")[0])}
                    alt={item.title}
                  />
                ) : (
                  <span className={styles.menuImagePlaceholder}>
                    <AppsRounded />
                  </span>
                )}
                <span className={styles.menuAddBadge}>
                  <Add fontSize="small" />
                </span>
              </div>
              <div className={styles.menuInfo}>
                <span className={styles.menuName}>
                  {item.title?.length > 28
                    ? `${item.title.substring(0, 28)}...`
                    : item.title}
                </span>
                {item.variants?.length > 0 && (
                  <span className={styles.menuVariants}>
                    {item.variants.map((varnt, index) => (
                      <span
                        key={`${varnt.variant}-${index}`}
                        className={styles.menuVariantChip}
                      >
                        {varnt.variant}: {varnt.variantValue}
                      </span>
                    ))}
                  </span>
                )}
                <span className={styles.menuPrice}>
                  {currency.currencySymbol}{" "}
                  {(
                    (Number(item.price) || 0) *
                    (1 - (Number(item.discount) || 0) / 100)
                  ).toFixed(2)}
                </span>
              </div>
            </button>
          ))
        )}
      </div>

      <Popover
        open={Boolean(variantAnchor)}
        anchorEl={variantAnchor}
        onClose={closeVariantPopover}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        slotProps={{ paper: { className: styles.variantPopover } }}
      >
        {variantItem && (
          <div className={styles.variantPopContent}>
            <div className={styles.variantPopTitle}>{variantItem.title}</div>
            {Object.entries(groupVariants(variantItem)).map(
              ([name, values]) => (
                <div key={name} className={styles.variantGroup}>
                  <span className={styles.variantGroupLabel}>{name}</span>
                  <div className={styles.variantOptions}>
                    {values.map((value) => (
                      <button
                        key={value}
                        type="button"
                        className={`${styles.variantOption} ${
                          variantSelections[name] === value
                            ? styles.variantOptionActive
                            : ""
                        }`}
                        onClick={() =>
                          setVariantSelections((prev) => ({
                            ...prev,
                            [name]: value,
                          }))
                        }
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>
              )
            )}
            <button
              type="button"
              className={styles.variantAddBtn}
              onClick={confirmVariantAdd}
            >
              <Add fontSize="small" /> Add to order
            </button>
          </div>
        )}
      </Popover>
    </div>
  );
};

export default RestaurantMenu;